import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { Plugin, PluginKey } from "prosemirror-state";
import Render from "./render";

import { getMarks } from "../utils/content";

const historyKey = "history$";
const closeHistoryKey = "closeHistory$";

// Builds a transaction that replaces a trailing abbreviation before the cursor with an
// abbreviation node, or returns null if none is found. `trailingDelimiter` is true when a
// delimiter char was just typed (cursor sits after the delimiter) and false for line-break
// keys (cursor sits directly after the abbreviation).
const buildAbbreviationReplacement = (state, regExEol, { trailingDelimiter }) => {
    const { selection } = state;
    if (!selection.empty) {
        return null;
    }

    const { $from } = selection;
    const node = $from.nodeBefore;
    if (!node?.isText || !node?.text) {
        return null;
    }

    const text = trailingDelimiter ? node.text.slice(0, -1) : node.text;
    if (!text) {
        return null;
    }

    regExEol.lastIndex = 0;
    const match = regExEol.exec(text);
    if (!match?.[0]) {
        return null;
    }

    const abrv = match[0];
    const replaceEnd = trailingDelimiter ? $from.pos - 1 : $from.pos;
    const replaceStart = replaceEnd - abrv.length;

    if (state.doc.resolve(replaceStart).parent !== state.doc.resolve(replaceEnd).parent) {
        return null;
    }

    const newNode = state.schema.nodes.abbreviation.create({ key: abrv }, null);
    const tr = state.tr;
    tr.replaceWith(replaceStart, replaceEnd, newNode);
    tr.setMeta(closeHistoryKey, true);

    return tr;
};

export default Node.create({
    name: "abbreviation",
    marks: "commentMark",
    group: "inline",
    inline: true,
    atom: true,

    onBeforeCreate() {
        this.storage.store = this.options.abbreviation || {};

        const readonly = !this.editor.options.editable;
        this.options.readonly = readonly;
        this.type.spec.selectable = !readonly;
    },

    addAttributes() {
        return {
            key: {
                default: null,
                required: true,
                parseHTML: (el) => el.getAttribute("data-key"),
                renderHTML: (attr) => ({ "data-key": attr.key }),
            },
            comment: {
                default: undefined,
            },
        };
    },

    addOptions() {
        return {
            HTMLAttributes: {},
            abbreviation: {},
            readonly: false,
        };
    },

    addStorage() {
        return {
            store: {},
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="abbreviation"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ["span", mergeAttributes(HTMLAttributes, { "data-type": "abbreviation" })];
    },

    addNodeView() {
        return ReactNodeViewRenderer(Render);
    },

    addCommands() {
        return {
            /**
             * Adds a new abbreviation entry to the content
             */
            addAbbreviationEntry: (options, surroundSpace = false) => ({ editor, commands }) => {
                const selection = editor.view.state.selection;
                const nodeBefore = selection.$from.nodeBefore;
                const nodeAfter = selection.$from.nodeAfter;
                const addSpaceBefore =
                    surroundSpace && nodeBefore && !nodeBefore.text?.endsWith(" ");
                const addSpaceAfter =
                    surroundSpace && nodeAfter && !nodeAfter.text?.startsWith(" ");

                const marks = getMarks({ editor });

                const content = [];
                if (addSpaceBefore) {
                    content.push({
                        type: "text",
                        text: " ",
                    });
                }

                content.push({
                    type: this.name,
                    attrs: options,
                    marks,
                });

                if (addSpaceAfter) {
                    content.push({
                        type: "text",
                        text: " ",
                    });
                }

                commands.insertContent(content);
            },

            /**
             * Replace text with abbreviation
             */
            replaceWithAbbreviation: ({ key, from, to }) => ({ editor, dispatch, tr }) => {
                const marks = getMarks({ editor });
                const node = editor.schema.nodeFromJSON({
                    type: this.name,
                    attrs: { key },
                    marks,
                });

                dispatch(tr.replaceWith(from, to, node));
            },

            /**
             * Updates the abbreviation values
             */
            setAbbreviation: (abbreviation) => async ({ editor }) => {
                editor.storage.abbreviation.store = abbreviation;
            },

            /**
             * Show a abbreviation picker
             */
            showAbbreviationPicker: () => async ({ editor }) => {
                editor.emit("abbreviationPicker");
            },

            /**
             * Create a abbreviation entry
             */
            createAbbreviationEntry: () => async ({ editor }) => {
                const { selection, doc } = editor.state;
                const text = doc.textBetween(selection.from, selection.to);

                editor.emit("abbreviationCreate", text);
            },
        };
    },

    addKeyboardShortcuts() {
        // Line-break keys don't insert a delimiter character, so they bypass the edit
        // handler below. Detect a trailing abbreviation here, then let the default split /
        // hard break still happen.
        const handleLineBreak = () => {
            const store = this.storage.store?.entryMap;
            if (store && Object.keys(store).length > 0) {
                const tr = buildAbbreviationReplacement(
                    this.editor.state,
                    this.storage.store.regExEol,
                    { trailingDelimiter: false },
                );
                if (tr) {
                    this.editor.view.dispatch(tr);
                }
            }

            return false;
        };

        return {
            "Alt-a": () => {
                this.editor.commands.showAbbreviationPicker();
            },
            Enter: handleLineBreak,
            "Shift-Enter": handleLineBreak,
            "Mod-Enter": handleLineBreak,
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey("abbreviationPasteHandler"),
                appendTransaction: (transactions, oldState, newState) => {
                    // Get the abbreviation store
                    const store = this.storage.store?.entryMap;
                    if (!store || Object.keys(store).length === 0) {
                        return null;
                    }

                    // Only process if there's actual content change
                    const docChanged = transactions.some((tr) => tr.docChanged);
                    if (!docChanged) {
                        return null;
                    }

                    // Check if this is a paste operation
                    const isPaste = transactions.some((tr) => {
                        return tr.getMeta("paste") || tr.getMeta("uiEvent") === "paste";
                    });
                    if (!isPaste) {
                        return;
                    }

                    const tr = newState.tr;
                    let hasChanges = false;

                    // STEP 1: Find abbreviation nodes that need to be unmarked
                    // because they were copied from another document and are no
                    // longer valid
                    const unmark = [];
                    newState.doc.descendants((node, pos) => {
                        if (node.type.name === "abbreviation") {
                            const key = node.attrs.key;
                            const found = store[key];

                            if (!found) {
                                unmark.push({
                                    from: pos,
                                    to: pos + node.nodeSize,
                                    text: node.attrs.key,
                                });
                            }
                        }
                    });

                    // Apply the changes in reverse order
                    unmark.reverse().forEach(({ from, to, text }) => {
                        tr.replaceWith(from, to, newState.schema.text(text));
                        hasChanges = true;
                    });

                    // STEP 2: Find any text nodes that may contain
                    // abbreviations and replace them with abbreviation nodes
                    const abrvRegEx = this.storage.store.regEx;

                    const mark = [];
                    newState.doc.descendants((node, pos) => {
                        if (!node.isText || !node.text) {
                            return;
                        }

                        let match;
                        while ((match = abrvRegEx.exec(node.text)) !== null) {
                            const abrv = match[0];
                            const abrvStart = match.index;

                            const from = pos + abrvStart;
                            const to = from + abrv.length;
                            const nodeAtPos = newState.doc.nodeAt(from);

                            if (!nodeAtPos || nodeAtPos.type.name !== "abbreviation") {
                                mark.push({
                                    from,
                                    to,
                                    abrv,
                                    marks: node.marks || [],
                                });
                            }
                        }
                    });

                    mark.reverse().forEach(({ from, to, abrv, marks }) => {
                        const node = newState.schema.nodes.abbreviation.create(
                            { key: abrv },
                            null,
                            marks,
                        );

                        tr.replaceWith(from, to, node);
                        hasChanges = true;
                    });

                    return hasChanges ? tr : null;
                },
            }),
            new Plugin({
                key: new PluginKey("abbreviationEditHandler"),
                appendTransaction: (transactions, oldState, newState) => {
                    // Get the abbreviation store
                    const store = this.storage.store?.entryMap;
                    if (!store || Object.keys(store).length === 0) {
                        return null;
                    }

                    // Only process if there's actual content change
                    const docChanged = transactions.some((tr) => tr.docChanged);
                    if (!docChanged) {
                        return null;
                    }

                    // Check if the selection is a cursor (a single point)
                    const { selection } = newState;
                    if (!selection.empty) {
                        return null;
                    }

                    const isHistory = transactions.some((tr) => tr.getMeta(historyKey));
                    if (isHistory) {
                        return null;
                    }

                    const processText = (n) => {
                        if (n.isText) {
                            insertedText += n.text;
                        }
                    };

                    // Collect all text inserted by these transactions
                    let insertedText = "";
                    for (const tr of transactions) {
                        for (const step of tr.steps) {
                            if (step.slice) {
                                step.slice.content.forEach(processText);
                            }
                        }
                    }

                    const lastChar = insertedText.slice(-1);
                    if (!lastChar || /[\w-]/.test(lastChar)) {
                        return null;
                    }

                    // Cursor sits after the delimiter, so the abbreviation ends one char before it.
                    return buildAbbreviationReplacement(newState, this.storage.store.regExEol, {
                        trailingDelimiter: true,
                    });
                },
            }),
        ];
    },
});
